/**
 * The prices a Trade Feed message carries, for the card and the popup alike,
 * so the two can never disagree.
 *
 * The server sends `prices`: approved AND unapproved together, each tagged
 * `approved`, the searched reference first (Alex, 2026-10-23: a price list
 * often has some watches approved and the rest only extracted). Responses from
 * the previous backend have only `trade_prices` / `extracted_prices`; those
 * are read as before (approved, else extracted) and tagged the same way.
 *
 * kind: "approved" | "extracted" | "none" — what the FIRST price is.
 */
const pricesOf = (item) => {
  if (Array.isArray(item?.prices)) {
    const prices = item.prices;
    const approvedCount = item.approved_count ?? prices.filter((p) => p.approved).length;
    return {
      kind: prices.length ? (prices[0].approved ? "approved" : "extracted") : "none",
      prices,
      count: item.price_count ?? prices.length,
      approvedCount,
      unapprovedCount: item.unapproved_count ?? prices.length - approvedCount,
    };
  }

  if (item?.trade_prices?.length) {
    const prices = item.trade_prices.map((p) => ({ approved: true, ...p }));
    return { kind: "approved", prices, count: prices.length, approvedCount: prices.length, unapprovedCount: 0 };
  }
  if (item?.extracted_prices?.length) {
    const count = item.extracted_count || item.extracted_prices.length;
    const prices = item.extracted_prices.map((p) => ({ approved: false, ...p }));
    return { kind: "extracted", prices, count, approvedCount: 0, unapprovedCount: count };
  }
  return { kind: "none", prices: [], count: 0, approvedCount: 0, unapprovedCount: 0 };
};

/** How long after it arrives an unread message still says "Processing". */
const PROCESSING_WINDOW_S = 6 * 3600;

/**
 * "Processing" instead of "No price" while the pipeline has not read the
 * message yet (Alex, 2026-10-23). Only for a recent message the server says is
 * unread: chat and photo-only posts are never read, and saying "Processing"
 * about them forever would be its own lie. Unknown (null) stays "No price".
 */
export const isProcessing = (item, now = Date.now() / 1000) =>
  item?.processed === false && now - Number(item.timestamp || 0) < PROCESSING_WINDOW_S;

export default pricesOf;
